// src/components/PacienteList.jsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://app-medifyhc.com';

const PacienteList = ({ pacientes, onListChange }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(isMdDown ? 8 : 12);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPatient, setMenuPatient] = useState(null);

  // Estados para ordenamiento y filtrado
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('nombreCompleto'); // Por defecto ordenamos por nombre
  const [filterOptions, setFilterOptions] = useState({
    sexo: 'todos',
    estado: 'todos'
  });
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState(null);

  // Función para corregir URLs de imágenes
  const fixImageUrl = (url) => {
    if (!url) return undefined;
    const u = `${url}`.trim();
    if (!u) return undefined;
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    if (u.startsWith('//')) return `https:${u}`;
    if (u.startsWith('/')) return `${API_BASE_URL}${u}`;
    return `${API_BASE_URL}/${u}`;
  };

  // Obtiene la mejor URL de foto disponible
  const getPhotoUrl = (p) => {
    const raw =
      p?.foto_perfil ||
      p?.foto_url ||
      p?.avatar_url ||
      p?.foto ||
      p?.imagen ||
      p?.profile_photo ||
      p?.image ||
      '';
    return fixImageUrl(raw);
  };

  // Manejadores menú opciones
  const handleMenuOpen = (event, patient) => {
    setAnchorEl(event.currentTarget);
    setMenuPatient(patient);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPatient(null);
  };

  // Filtros
  const handleFilterMenuOpen = (event) => setFilterMenuAnchorEl(event.currentTarget);
  const handleFilterMenuClose = () => setFilterMenuAnchorEl(null);
  const handleFilterChange = (filterType, value) => {
    setFilterOptions(prev => ({ ...prev, [filterType]: value }));
    setPage(1);
    handleFilterMenuClose();
  };

  // Ordenamiento
  const handleSortMenuOpen = (event) => setSortMenuAnchorEl(event.currentTarget);
  const handleSortMenuClose = () => setSortMenuAnchorEl(null);
  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(1);
    handleSortMenuClose();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar a este paciente?')) return;
    const token = localStorage.getItem('authToken');
    try {
      await axios.delete(`${API_BASE_URL}/api/pacientes/${id}/`, {
        headers: { Authorization: `Token ${token}` }
      });
      toast.success('¡Paciente eliminado con éxito!');
      if (onListChange) onListChange();
    } catch (error) {
      toast.error('Hubo un error al eliminar el paciente.');
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  // Filtrar y ordenar pacientes
  const filteredAndSortedPatients = pacientes
    .filter(paciente => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const nombreCompleto = `${paciente.nombres || ''} ${paciente.apellidos || ''}`.toLowerCase();
        const dni = paciente.dni ? paciente.dni.toLowerCase() : '';
        const email = paciente.email ? paciente.email.toLowerCase() : '';
        const telefono = paciente.telefono_principal ? `${paciente.telefono_principal}`.toLowerCase() : '';

        if (
          !nombreCompleto.includes(searchLower) &&
          !dni.includes(searchLower) &&
          !email.includes(searchLower) &&
          !telefono.includes(searchLower)
        ) {
          return false;
        }
      }

      if (filterOptions.sexo !== 'todos' && paciente.sexo !== filterOptions.sexo) {
        return false;
      }

      if (filterOptions.estado !== 'todos') {
        const isActive = paciente.activo === true || paciente.estado === 'activo';
        if ((filterOptions.estado === 'activo' && !isActive) ||
            (filterOptions.estado === 'inactivo' && isActive)) {
          return false;
        }
      }

      return true;
    })
    .map(p => ({
      ...p,
      id: p.id_paciente ?? p.id,
      nombreCompleto: `${p.nombres || ''} ${p.apellidos || ''}`.trim()
    }))
    .sort((a, b) => {
      let valueA, valueB;

      switch (sortField) {
        case 'nombreCompleto':
          valueA = (a.nombreCompleto || '').toLowerCase();
          valueB = (b.nombreCompleto || '').toLowerCase();
          break;
        case 'edad':
          valueA = a.age || 0;
          valueB = b.age || 0;
          break;
        case 'email':
          valueA = (a.email || '').toLowerCase();
          valueB = (b.email || '').toLowerCase();
          break;
        case 'telefono':
          valueA = a.telefono_principal || '';
          valueB = b.telefono_principal || '';
          break;
        case 'dni':
          valueA = a.dni || '';
          valueB = b.dni || '';
          break;
        case 'ultima_consulta':
          valueA = new Date(a.ultima_consulta || '1900-01-01');
          valueB = new Date(b.ultima_consulta || '1900-01-01');
          break;
        default:
          valueA = a[sortField] || '';
          valueB = b[sortField] || '';
      }

      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
      }
    });

  // Paginación
  const indexOfLastPatient = page * rowsPerPage;
  const indexOfFirstPatient = indexOfLastPatient - rowsPerPage;
  const currentPatients = filteredAndSortedPatients.slice(indexOfFirstPatient, indexOfLastPatient);
  const totalPages = Math.ceil(filteredAndSortedPatients.length / rowsPerPage);

  // Auxiliares
  const getInitials = (name) =>
    (name || '')
      .split(' ')
      .filter(Boolean)
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);

  const getAvatarColor = (sexo) => {
    return sexo === 'Masculino' ? theme.palette.primary.main : theme.palette.secondary.main;
  };

  // Helper: clave única robusta (nunca vacía)
  const buildCardKey = (p, idx) => {
    const rawId = p?.id ?? p?.id_paciente ?? p?.uuid ?? p?.pk ?? '';
    const idPart = (typeof rawId === 'number' ? String(rawId) : String(rawId || '')).trim() || `temp-${idx}`;

    const dni = p?.dni || p?.identidad || p?.numero_identidad || '';
    const dniPart = String(dni || 'nodni').trim() || 'nodni';

    return `pl-${idPart}-${dniPart}-${idx}`;
  };

  // Helper: ID seguro para rutas
  const getSafeRouteId = (p, idx) => {
    const rawId = p?.id ?? p?.id_paciente ?? p?.uuid ?? p?.pk ?? '';
    const idStr = (typeof rawId === 'number' ? String(rawId) : String(rawId || '')).trim();
    return idStr || null;
  };

  // Id derivado del paciente seleccionado en el menú
  const menuRouteId = menuPatient ? getSafeRouteId(menuPatient) : null;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Barra de búsqueda y filtros */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: 3,
          borderRadius: '16px',
          boxShadow: theme.shadows[1],
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar paciente por nombre, DNI, correo o teléfono..."
              value={searchTerm}
              onChange={handleSearchChange}
              size={isSmDown ? 'small' : 'medium'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Limpiar búsqueda"
                      onClick={handleClearSearch}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '12px',
                  '&:hover': {
                    boxShadow: theme.shadows[1],
                  },
                  transition: 'box-shadow 0.3s ease'
                }
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={handleFilterMenuOpen}
              size={isSmDown ? 'small' : 'medium'}
              sx={{
                borderRadius: '12px',
                height: isSmDown ? 44 : 56
              }}
              aria-label="Abrir filtros"
            >
              Filtrar
            </Button>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SortIcon />}
              onClick={handleSortMenuOpen}
              size={isSmDown ? 'small' : 'medium'}
              sx={{
                borderRadius: '12px',
                height: isSmDown ? 44 : 56
              }}
              aria-label="Abrir opciones de ordenamiento"
            >
              Ordenar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Resultados */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Mostrando {currentPatients.length} de {filteredAndSortedPatients.length} pacientes
        </Typography>
      </Box>

      {/* Grid de tarjetas de pacientes */}
      {currentPatients.length > 0 ? (
        <Grid container spacing={2.5}>
          {currentPatients.map((paciente, idx) => {
            const cardKey = buildCardKey(paciente, idx) || `pl-fallback-${idx}`;
            const routeId = getSafeRouteId(paciente, idx); // puede ser null

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={cardKey}>
                <Card
                  elevation={0}
                  variant="outlined"
                  sx={{
                    borderRadius: '16px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: theme.shadows[3],
                      transform: 'translateY(-2px)'
                    },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: theme.palette.background.paper
                  }}
                >
                  {/* Header de la tarjeta */}
                  <Box
                    sx={{
                      p: { xs: 1.5, md: 2 },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      position: 'relative',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.08 : 0.04)
                    }}
                  >
                    <Avatar
                      src={getPhotoUrl(paciente)}
                      alt={paciente.nombreCompleto}
                      sx={{
                        bgcolor: getAvatarColor(paciente.sexo),
                        width: isSmDown ? 44 : 48,
                        height: isSmDown ? 44 : 48,
                        flexShrink: 0
                      }}
                      imgProps={{
                        referrerPolicy: 'no-referrer',
                        onError: (e) => { e.currentTarget.src = ''; } // fallback a iniciales
                      }}
                    >
                      {getInitials(paciente.nombreCompleto)}
                    </Avatar>

                    {/* Contenedor de nombre y edad */}
                    <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 6, md: 7 } }}>
                      <Typography
                        variant={isSmDown ? 'subtitle1' : 'h6'}
                        sx={{
                          fontWeight: 700,
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                          hyphens: 'auto',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.25
                        }}
                        title={paciente.nombreCompleto}
                      >
                        {paciente.nombreCompleto || 'Paciente sin nombre'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {paciente.age ? `${paciente.age} años` : 'Edad no disponible'}
                      </Typography>
                    </Box>

                    <Tooltip title="Opciones">
                      <IconButton
                        aria-label="Opciones"
                        onClick={(e) => handleMenuOpen(e, paciente)}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                        size="small"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Contenido */}
                  <CardContent sx={{ flexGrow: 1, py: 2 }}>
                    <Stack spacing={1.25}>
                      {paciente.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                          <EmailIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                          <Typography
                            variant="body2"
                            noWrap
                            title={paciente.email}
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            {paciente.email}
                          </Typography>
                        </Box>
                      )}

                      {paciente.telefono_principal && (
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                          <PhoneIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2">
                            {paciente.telefono_principal}
                          </Typography>
                        </Box>
                      )}

                      {paciente.dni && (
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                          <BadgeIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2">
                            {paciente.dni}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>

                  <Divider />

                  {/* Acciones */}
                  <CardActions
                    sx={{
                      p: 2,
                      pt: 1.5,
                      display: 'flex',
                      gap: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      flexWrap: 'wrap'
                    }}
                  >
                    <Button
                      component={Link}
                      to={routeId ? `/doctor/pacientes/${routeId}` : '#'}
                      variant="outlined"
                      size={isSmDown ? 'small' : 'medium'}
                      startIcon={<VisibilityIcon />}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        flex: 1,
                        minWidth: 120,
                        '&:hover': {
                          bgcolor: isDarkMode
                            ? alpha(theme.palette.primary.main, 0.1)
                            : alpha(theme.palette.primary.main, 0.05),
                        }
                      }}
                      aria-label="Ver detalles del paciente"
                      onClick={(e) => {
                        if (!routeId) {
                          e.preventDefault();
                          toast.info('Paciente sin ID');
                        }
                      }}
                    >
                      Ver
                    </Button>

                    <Button
                      component={Link}
                      to={routeId ? `/doctor/pacientes/${routeId}/nueva-consulta` : '#'}
                      variant="contained"
                      size={isSmDown ? 'small' : 'medium'}
                      startIcon={<EventNoteIcon />}
                      sx={{ borderRadius: '12px', textTransform: 'none', flex: 1, minWidth: 140, boxShadow: 'none' }}
                      aria-label="Crear nueva consulta"
                      onClick={(e) => {
                        if (!routeId) {
                          e.preventDefault();
                          toast.info('Paciente sin ID');
                        }
                      }}
                    >
                      Consulta
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: '16px',
            bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.08 : 0.04)
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm
              ? `No se encontraron pacientes que coincidan con "${searchTerm}"`
              : 'No hay pacientes registrados'}
          </Typography>

          {searchTerm && (
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              startIcon={<ClearIcon />}
              sx={{ mt: 2, borderRadius: '12px' }}
            >
              Limpiar búsqueda
            </Button>
          )}
        </Paper>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            color="primary"
            size={isSmDown ? 'small' : 'medium'}
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: '12px',
              }
            }}
          />
        </Box>
      )}

      {/* Menú de opciones */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: '12px',
            minWidth: 180,
            overflow: 'visible',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          component={Link}
          to={menuRouteId ? `/doctor/pacientes/${menuRouteId}` : '#'}
          onClick={(e) => {
            if (!menuRouteId) {
              e.preventDefault();
              toast.info('Paciente sin ID');
            }
            handleMenuClose();
          }}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          <VisibilityIcon fontSize="small" sx={{ mr: 2 }} />
          Ver detalles
        </MenuItem>
        <MenuItem
          component={Link}
          to={menuRouteId ? `/doctor/pacientes/${menuRouteId}/edit` : '#'}
          onClick={(e) => {
            if (!menuRouteId) {
              e.preventDefault();
              toast.info('Paciente sin ID');
            }
            handleMenuClose();
          }}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          <EditIcon fontSize="small" sx={{ mr: 2 }} />
          Editar
        </MenuItem>
        <MenuItem
          component={Link}
          to={menuRouteId ? `/doctor/pacientes/${menuRouteId}/nueva-consulta` : '#'}
          onClick={(e) => {
            if (!menuRouteId) {
              e.preventDefault();
              toast.info('Paciente sin ID');
            }
            handleMenuClose();
          }}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          <EventNoteIcon fontSize="small" sx={{ mr: 2 }} />
          Nueva consulta
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={() => {
            if (menuPatient && menuRouteId) {
              handleDelete(menuRouteId);
            } else {
              toast.info('Paciente sin ID');
            }
            handleMenuClose();
          }}
          sx={{
            color: 'error.main',
            borderRadius: '8px',
            mx: 1,
            my: 0.5,
            '&:hover': {
              bgcolor: alpha(theme.palette.error.main, 0.1),
            }
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 2 }} />
          Eliminar
        </MenuItem>
      </Menu>

      {/* Menú de filtros */}
      <Menu
        anchorEl={filterMenuAnchorEl}
        open={Boolean(filterMenuAnchorEl)}
        onClose={handleFilterMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: '12px',
            minWidth: 200,
            overflow: 'visible',
            mt: 1.5,
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
          Filtrar por sexo
        </Typography>
        <MenuItem
          onClick={() => handleFilterChange('sexo', 'todos')}
          selected={filterOptions.sexo === 'todos'}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          Todos
        </MenuItem>
        <MenuItem
          onClick={() => handleFilterChange('sexo', 'Masculino')}
          selected={filterOptions.sexo === 'Masculino'}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          Masculino
        </MenuItem>
        <MenuItem
          onClick={() => handleFilterChange('sexo', 'Femenino')}
          selected={filterOptions.sexo === 'Femenino'}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          Femenino
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
          Filtrar por estado
        </Typography>
        <MenuItem
          onClick={() => handleFilterChange('estado', 'todos')}
          selected={filterOptions.estado === 'todos'}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          Todos
        </MenuItem>
        <MenuItem
          onClick={() => handleFilterChange('estado', 'activo')}
          selected={filterOptions.estado === 'activo'}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          Activos
        </MenuItem>
        <MenuItem
          onClick={() => handleFilterChange('estado', 'inactivo')}
          selected={filterOptions.estado === 'inactivo'}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          Inactivos
        </MenuItem>
      </Menu>

      {/* Menú de ordenamiento */}
      <Menu
        anchorEl={sortMenuAnchorEl}
        open={Boolean(sortMenuAnchorEl)}
        onClose={handleSortMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: '12px',
            minWidth: 200,
            overflow: 'visible',
            mt: 1.5,
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
          Ordenar por
        </Typography>
        <MenuItem
          onClick={() => handleSortChange('nombreCompleto')}
          selected={sortField === 'nombreCompleto'}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <Typography>Nombre</Typography>
            {sortField === 'nombreCompleto' && (
              sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
            )}
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => handleSortChange('edad')}
          selected={sortField === 'edad'}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <Typography>Edad</Typography>
            {sortField === 'edad' && (
              sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
            )}
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => handleSortChange('dni')}
          selected={sortField === 'dni'}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <Typography>DNI</Typography>
            {sortField === 'dni' && (
              sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
            )}
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => handleSortChange('ultima_consulta')}
          selected={sortField === 'ultima_consulta'}
          sx={{ borderRadius: '8px', mx: 1, my: 0.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <Typography>Última consulta</Typography>
            {sortField === 'ultima_consulta' && (
              sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
            )}
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PacienteList;
